import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Paper,
  Tooltip,
  Autocomplete,
  Stack
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

// Calendar Event tipi - Özel etkinlikler için
interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  createdAt: number;
  isChecked?: boolean;
  color?: string; // Özel etkinlik rengi
}

// Tek bir etkinlik için veri tipi
interface EventFormItem {
  title: string;
  start: Date;
  end: Date;
  specialEventId?: string; // İlişkilendirilmiş özel etkinliğin ID'si
  eventColor?: string; // Etkinliğin rengi
}

interface BulkEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (eventsData: EventFormItem[]) => Promise<string[]>;
  calendarEvents: CalendarEvent[]; // Mevcut özel etkinlikler listesi
}

const BulkEventModal: React.FC<BulkEventModalProps> = ({
  open,
  onClose,
  onSave,
  calendarEvents
}) => {
  const { t, i18n } = useTranslation('other');
  const adapterLocale = i18n.language?.startsWith('tr') ? tr : enUS;
  // Seçilen özel etkinlik
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<CalendarEvent | null>(null);
  
  // Çoklu etkinlik dizisi
  const [events, setEvents] = useState<EventFormItem[]>([
    {
      title: '',
      start: new Date(),
      end: new Date(new Date().setHours(new Date().getHours() + 1))
    }
  ]);
  
  // Validation ve loading state'leri
  const [errors, setErrors] = useState<{[key: number]: {title?: boolean, time?: boolean}}>({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Snackbar için state'ler
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');

  // Form açıldığında içeriği sıfırla
  useEffect(() => {
    if (open) {
      // Varsayılan değerlerle başla
      setEvents([
        {
          title: '',
          start: new Date(),
          end: new Date(new Date().setHours(new Date().getHours() + 1))
        }
      ]);
      setSelectedCalendarEvent(null);
      setErrors({});
    }
  }, [open]);

  // Özel etkinlik seçildiğinde tüm etkinliklere uygula
  useEffect(() => {
    if (selectedCalendarEvent) {
      // Tüm etkinliklere özel etkinlik ID'sini ve rengini ekle
      const updatedEvents = events.map(event => ({
        ...event,
        specialEventId: selectedCalendarEvent.id,
        eventColor: selectedCalendarEvent.color
      }));
      setEvents(updatedEvents);
    } else {
      // Özel etkinlik seçimi kaldırıldığında, tüm etkinliklerden specialEventId'yi kaldır
      const updatedEvents = events.map(event => {
        const { specialEventId, eventColor, ...rest } = event;
        return rest;
      });
      setEvents(updatedEvents);
    }
  }, [selectedCalendarEvent]);

  // Etkinlik başlığı değiştiğinde
  const handleTitleChange = (index: number, value: string) => {
    const updatedEvents = [...events];
    updatedEvents[index].title = value;
    setEvents(updatedEvents);
    
    // Hata durumunu güncelle
    if (value.trim() === '') {
      setErrors({
        ...errors,
        [index]: { ...errors[index], title: true }
      });
    } else {
      // Hatayı temizle
      const updatedErrors = { ...errors };
      if (updatedErrors[index]) {
        updatedErrors[index] = { ...updatedErrors[index], title: false };
      }
      setErrors(updatedErrors);
    }
  };

  // Başlangıç zamanı değiştiğinde
  const handleStartChange = (index: number, date: Date | null) => {
    if (!date) return;
    
    const updatedEvents = [...events];
    updatedEvents[index].start = date;
    
    // Başlangıç tarihi bitiş tarihinden sonra olmamalı
    const hasTimeError = date > updatedEvents[index].end;
    
    if (hasTimeError) {
      setErrors({
        ...errors,
        [index]: { ...errors[index], time: true }
      });
    } else {
      // Hatayı temizle
      const updatedErrors = { ...errors };
      if (updatedErrors[index]) {
        updatedErrors[index] = { ...updatedErrors[index], time: false };
      }
      setErrors(updatedErrors);
    }
    
    setEvents(updatedEvents);
  };

  // Bitiş zamanı değiştiğinde
  const handleEndChange = (index: number, date: Date | null) => {
    if (!date) return;
    
    const updatedEvents = [...events];
    updatedEvents[index].end = date;
    
    // Bitiş tarihi başlangıç tarihinden önce olmamalı
    const hasTimeError = date < updatedEvents[index].start;
    
    if (hasTimeError) {
      setErrors({
        ...errors,
        [index]: { ...errors[index], time: true }
      });
    } else {
      // Hatayı temizle
      const updatedErrors = { ...errors };
      if (updatedErrors[index]) {
        updatedErrors[index] = { ...updatedErrors[index], time: false };
      }
      setErrors(updatedErrors);
    }
    
    setEvents(updatedEvents);
  };

  // Yeni bir etkinlik eklemek için
  const handleAddEvent = () => {
    // Var olan etkinliklerden son etkinliğin zamanlarını al
    const lastEvent = events[events.length - 1];
    
    // Yeni etkinlik için son etkinliğin bitiş zamanından bir saat sonrasını başlangıç yap
    const newStart = new Date(lastEvent.end);
    newStart.setHours(newStart.getHours() + 1);
    
    // Bitiş zamanını başlangıçtan bir saat sonra yap
    const newEnd = new Date(newStart);
    newEnd.setHours(newEnd.getHours() + 1);
    
    // Yeni etkinliği diziye ekle
    const newEvent: EventFormItem = {
      title: '',
      start: newStart,
      end: newEnd
    };
    
    // Eğer özel etkinlik seçili ise, onun bilgilerini de ekle
    if (selectedCalendarEvent) {
      newEvent.specialEventId = selectedCalendarEvent.id;
      newEvent.eventColor = selectedCalendarEvent.color;
    }
    
    setEvents([...events, newEvent]);
  };

  // Etkinlik silme
  const handleRemoveEvent = (index: number) => {
    // Eğer tek etkinlik kaldıysa silme işlemi yapmayalım
    if (events.length <= 1) {
      setSnackbarMessage(t('events.bulkEventModal.messages.minOneEvent'));
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    // Etkinliği diziden kaldır
    const updatedEvents = events.filter((_, i) => i !== index);
    setEvents(updatedEvents);
    
    // Etkinlik silindikten sonra hata state'ini güncelle
    const updatedErrors = { ...errors };
    delete updatedErrors[index];
    
    // İndeksleri yeniden düzenle
    const newErrors: {[key: number]: {title?: boolean, time?: boolean}} = {};
    Object.entries(updatedErrors).forEach(([key, value]) => {
      const numKey = parseInt(key);
      if (numKey > index) {
        newErrors[numKey - 1] = value;
      } else {
        newErrors[numKey] = value;
      }
    });
    
    setErrors(newErrors);
  };

  // Form validasyonu ve kaydetme işlemi
  const handleSubmit = async () => {
    // Tüm etkinlikleri kontrol et
    let hasError = false;
    const newErrors: {[key: number]: {title?: boolean, time?: boolean}} = {};
    
    events.forEach((event, index) => {
      const eventErrors: {title?: boolean, time?: boolean} = {};
      
      // Başlık zorunlu
      if (event.title.trim() === '') {
        eventErrors.title = true;
        hasError = true;
      }
      
      // Zaman kontrolü
      if (event.start >= event.end) {
        eventErrors.time = true;
        hasError = true;
      }
      
      // Eğer hata varsa diziye ekle
      if (Object.keys(eventErrors).length > 0) {
        newErrors[index] = eventErrors;
      }
    });
    
    // Hata varsa kaydetme
    if (hasError) {
      setErrors(newErrors);
      setSnackbarMessage(t('events.bulkEventModal.messages.fillAllFields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Kaydetme işlemi
    setIsSaving(true);
    
    try {
      // onSave fonksiyonunu çağır
      await onSave(events);
      
      // Başarılı olduğunda modalı kapat
      handleClose();
    } catch (error) {
      console.error('Error while saving bulk events:', error);
      setSnackbarMessage(t('events.bulkEventModal.messages.saveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Modalı kapatma işlemi
  const handleClose = () => {
    // Event state'lerini temizle
    setEvents([
      {
        title: '',
        start: new Date(),
        end: new Date(new Date().setHours(new Date().getHours() + 1))
      }
    ]);
    setSelectedCalendarEvent(null);
    setErrors({});
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md" // Daha geniş bir modal
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#FF9800', // Turuncu başlık
          color: 'white'
        }}>
          <Typography variant="h6">
            {t('events.bulkEventModal.title')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          {/* Bilgilendirme kutusu */}
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: 'info.light', 
            borderRadius: 1, 
            color: 'info.contrastText',
            display: 'flex',
            alignItems: 'center'
          }}>
            <InfoOutlinedIcon sx={{ mr: 1 }} />
            <Typography variant="body2">
              {t('events.bulkEventModal.info')}
            </Typography>
          </Box>
          
          {/* Özel Etkinlik Seçimi */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
              {t('events.bulkEventModal.specialEventSelectionTitle')}
            </Typography>
            
            <Autocomplete
              options={calendarEvents}
              getOptionLabel={(option) => option.title}
              value={selectedCalendarEvent}
              onChange={(event, newValue) => {
                setSelectedCalendarEvent(newValue);
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LinkIcon sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.title}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {option.description.length > 50 
                            ? `${option.description.substring(0, 50)}...` 
                            : option.description}
                        </Typography>
                      )}
                    </Box>
                    {/* Renk göstergesi */}
                    {option.color && (
                      <Box 
                        sx={{ 
                          ml: 'auto', 
                          width: 16, 
                          height: 16, 
                          borderRadius: '50%', 
                          backgroundColor: option.color,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }} 
                      />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('events.bulkEventModal.fields.specialEvent')}
                  variant="outlined"
                  placeholder={t('events.bulkEventModal.fields.specialEventPlaceholder')}
                  fullWidth
                />
              )}
            />
            
            {selectedCalendarEvent ? (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  borderLeft: `6px solid ${selectedCalendarEvent.color || '#1976d2'}`
                }}
              >
                <LinkIcon sx={{ mr: 1, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {t('events.bulkEventModal.selectedSpecialEvent', { title: selectedCalendarEvent.title })}
                  </Typography>
                  <Typography variant="caption">
                    {t('events.bulkEventModal.selectedSpecialEventInfo')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('events.bulkEventModal.noSpecialEventInfo')}
              </Typography>
            )}
          </Paper>
          
          {/* Etkinlikler Listesi */}
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
            {t('events.bulkEventModal.eventInfoTitle')}
          </Typography>
          
          {events.map((event, index) => (
            <Paper 
              key={index} 
              elevation={1} 
              sx={{ 
                p: 2, 
                mb: 2, 
                borderRadius: 2,
                borderLeft: selectedCalendarEvent ? 
                  `6px solid ${selectedCalendarEvent.color || '#1976d2'}` : 
                  'none'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="medium">
                  {t('events.bulkEventModal.eventNumber', { number: index + 1 })}
                </Typography>
                
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => handleRemoveEvent(index)}
                  disabled={events.length <= 1} // Tek etkinlik kaldıysa silme butonunu devre dışı bırak
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={t('events.eventForm.fields.title')}
                    value={event.title}
                    onChange={(e) => handleTitleChange(index, e.target.value)}
                    fullWidth
                    variant="outlined"
                    error={errors[index]?.title}
                    helperText={errors[index]?.title ? t('events.bulkEventModal.validation.titleRequired') : ''}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label={t('events.eventForm.fields.startTime')}
                    value={event.start}
                    onChange={(date) => handleStartChange(index, date)}
                    format="dd MMM yyyy HH:mm"
                    ampm={false}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        variant: 'outlined',
                        error: errors[index]?.time,
                        helperText: errors[index]?.time ? t('events.bulkEventModal.validation.invalidDateRange') : ''
                      } 
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label={t('events.eventForm.fields.endTime')}
                    value={event.end}
                    onChange={(date) => handleEndChange(index, date)}
                    format="dd MMM yyyy HH:mm"
                    ampm={false}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        variant: 'outlined',
                        error: errors[index]?.time,
                        helperText: errors[index]?.time ? t('events.bulkEventModal.validation.invalidDateRange') : ''
                      } 
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
          
          {/* Yeni Etkinlik Ekle Butonu */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddEvent}
            sx={{ mt: 1, mb: 2 }}
          >
            {t('events.bulkEventModal.actions.addNewEvent')}
          </Button>
          
          {/* Toplam etkinlik sayısı bilgisi */}
          <Typography variant="body2" color="text.secondary" align="center">
            {t('events.bulkEventModal.totalEvents', { count: events.length })}
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSaving}>
            {t('events.bulkEventModal.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSaving || events.length === 0}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? t('events.bulkEventModal.actions.saving') : t('events.bulkEventModal.actions.saveAll')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default BulkEventModal; 